import { RoleNames } from 'core';
import { AppMenuModel, DefaultAppMenuModel, DefaultAppMenuItemModel, AppMenuItemType } from 'containers/App/AppMenuBar';
import { ADDONFEATURE } from 'core/agency/AddonFeature';
import { hasRoles, notRoles, addOnEnabled, hasFuncs } from 'core/permission/PermissionDSL';
import { faChartBar, faCheckSquare, faBriefcase, faCog } from '@fortawesome/free-solid-svg-icons';
import productMenuIcon from 'assets/product-menu-icon.svg';
import { compact } from 'lodash';
import { Permission } from 'core/auth/Permission';
import { ACCOUNT_HEALTH_CHECK, ADVERTISING_STRATEGY, AI_BUDGET_PLANNER, MANAGED_SERVICE_REQUEST, ORDER_OPERATIONS, RMN_UNIVERSITY, SEGMENT_TUTORIAL } from './pageUrls';

export default function defaultMenus (): Array<AppMenuModel> {

  const { sysAdmin, agencyAdmin, agencyReport, adsReport, agencyManager } = RoleNames;

  const mainPageItems = [
    new DefaultAppMenuItemModel('appMenus.report.items.campaignDashboard', '/reports/campaign-dashboard', hasRoles(sysAdmin, agencyAdmin))
  ];
  const mainPageMenu = new DefaultAppMenuModel('appMenus.mainPage.title', mainPageItems, faCheckSquare);

  const reportMenuItems = [
    new DefaultAppMenuItemModel('appMenus.report.items.performance', '/reports/performance'),
    new DefaultAppMenuItemModel('appMenus.report.items.storedValue', '/stored-value', hasRoles(sysAdmin).or(hasRoles(agencyAdmin, agencyManager).and(addOnEnabled(ADDONFEATURE.COMPANY.STORED_VALUE)))),
    new DefaultAppMenuItemModel('appMenus.report.items.tenmaxReconciliation', '/reports/tenmax-reconciliation', hasRoles(sysAdmin)),
    new DefaultAppMenuItemModel('appMenus.report.items.storedValueReconciliation', '/reports/stored-value-reconciliation', hasRoles(sysAdmin))
  ];
  const reportMenu = new DefaultAppMenuModel('appMenus.report.title', reportMenuItems, faChartBar);

  const companyMenuItems = [
    new DefaultAppMenuItemModel('appMenus.comapnyManagement.items.agencies', '/agencies', hasRoles(sysAdmin, agencyAdmin)),
    new DefaultAppMenuItemModel('appMenus.comapnyManagement.items.advertisers', '/advertisers', hasRoles(sysAdmin, agencyAdmin)),
    new DefaultAppMenuItemModel('appMenus.comapnyManagement.items.accounts', '/accounts', hasRoles(sysAdmin)),
    new DefaultAppMenuItemModel('appMenus.comapnyManagement.items.cloudCosts', '/cloud-costs', hasRoles(sysAdmin))
    // new DefaultAppMenuItemModel('appMenus.comapnyManagement.items.pmp', '/pmp', hasRoles(sysAdmin))
  ];
  const companyMenu = new DefaultAppMenuModel('appMenus.comapnyManagement.title', companyMenuItems, faBriefcase);

  const retailRMNMenuItems = [
    new DefaultAppMenuItemModel('appMenus.retailRMN.items.products', '/products', notRoles(sysAdmin, adsReport).and(addOnEnabled(ADDONFEATURE.CHANNEL.RTB)))
  ];

  const retailRMNMenu = new DefaultAppMenuModel('appMenus.retailRMN.title', retailRMNMenuItems, productMenuIcon);

  const segmentMenuItems = [
    new DefaultAppMenuItemModel(
      'appMenus.segmentManagement.items.ecSegment',
      '/ec-segments',
      hasFuncs(Permission.EC_SEGMENT_READ).and(
        addOnEnabled(ADDONFEATURE.COMPANY.SEGMENT_MANAGEMENT)
      )
    ),
    // 這條導外教學沿用 ecSegment 的 guard,而不是像其他導外連結那樣傳 null。
    // DefaultAppMenuModel.visible 是底下各項的 OR、而 permission=null 的項目恆可見
    // (AppMenuBarModel.ts)⇒ 傳 null 會讓「受眾包管理」整個群組對沒有 EC_SEGMENT_READ
    // 或未開通 SEGMENT_MANAGEMENT 的供應商也浮出來,裡面只裝著一份他們沒有的功能的教學。
    new DefaultAppMenuItemModel(
      'appMenus.segmentManagement.items.segmentTutorial',
      SEGMENT_TUTORIAL,
      hasFuncs(Permission.EC_SEGMENT_READ).and(
        addOnEnabled(ADDONFEATURE.COMPANY.SEGMENT_MANAGEMENT)
      ),
      AppMenuItemType.OPEN_NEW
    )
  ];

  const segmentMenu = new DefaultAppMenuModel('appMenus.segmentManagement.title', segmentMenuItems, productMenuIcon);

  const operationMenuItems = [
    new DefaultAppMenuItemModel('appMenus.operation.items.orderOperations', ORDER_OPERATIONS, null, AppMenuItemType.OPEN_NEW),
    new DefaultAppMenuItemModel('appMenus.operation.items.advertisingStrategy', ADVERTISING_STRATEGY, null, AppMenuItemType.OPEN_NEW),
    new DefaultAppMenuItemModel('appMenus.operation.items.rmnUniversity', RMN_UNIVERSITY, null, AppMenuItemType.OPEN_NEW),
    new DefaultAppMenuItemModel('appMenus.operation.items.aiBudgetPlanner', AI_BUDGET_PLANNER, null, AppMenuItemType.OPEN_NEW),
    new DefaultAppMenuItemModel('appMenus.operation.items.creativePolicy', '/creative-policy', hasRoles(sysAdmin).or(addOnEnabled(ADDONFEATURE.CREATIVES.RICH_PRODUCT_NATIVE)))
  ];
  const operationMenu = new DefaultAppMenuModel('appMenus.operation.title', operationMenuItems, faCog);

  const customerServiceMenuItems = [
    new DefaultAppMenuItemModel('appMenus.customerService.items.onlineCustomerService', 'https://lin.ee/sMgcBcE', null, AppMenuItemType.OPEN_NEW),
    new DefaultAppMenuItemModel('appMenus.customerService.items.briefingSession', ACCOUNT_HEALTH_CHECK, null, AppMenuItemType.OPEN_NEW),
    new DefaultAppMenuItemModel('appMenus.customerService.items.operationPlan', MANAGED_SERVICE_REQUEST, null, AppMenuItemType.OPEN_NEW),
    new DefaultAppMenuItemModel('appMenus.customerService.items.FAQ', '/faq')
  ];
  const customerServiceMenu = new DefaultAppMenuModel('appMenus.customerService.title', customerServiceMenuItems, faCog);

  const advertisementMenuItems = [
    new DefaultAppMenuItemModel('appMenus.ads.items.orders', '/orders', notRoles(sysAdmin, adsReport)),
    new DefaultAppMenuItemModel('appMenus.ads.items.creatives', '/creatives', notRoles(sysAdmin, agencyReport, adsReport))
  ];
  const advertisementMenu = new DefaultAppMenuModel('appMenus.ads.title', advertisementMenuItems, faCheckSquare);

  const systemMenuItems = [
    new DefaultAppMenuItemModel('appMenus.system.items.bidAdjustment', '/bid-adjustments', hasRoles(sysAdmin)),
    new DefaultAppMenuItemModel('appMenus.system.items.revenueSharing', '/revenue-sharings', hasRoles(sysAdmin))
  ];
  const systemMenu = new DefaultAppMenuModel('appMenus.system.title', systemMenuItems, faCog);

  const announcementMenuItems = [
    new DefaultAppMenuItemModel(
      'appMenus.announcement.items.siteAnnouncement',
      '/site-announcements/system',
      hasFuncs(Permission.SITE_ANNOUNCEMENT_LIST_READ)
    ),
    new DefaultAppMenuItemModel(
      'appMenus.announcement.items.promotion',
      '/site-announcements/promotions',
      hasFuncs(Permission.SITE_ANNOUNCEMENT_LIST_READ)
    )
  ];
  const announcementMenu = new DefaultAppMenuModel('appMenus.announcement.title', announcementMenuItems, faBriefcase);

  const menus = compact([
    mainPageMenu,
    advertisementMenu,
    retailRMNMenu,
    segmentMenu,
    reportMenu,
    companyMenu,
    announcementMenu,
    operationMenu,
    customerServiceMenu,
    systemMenu
  ]);
  return menus;
}
